import type { ReactNode } from 'react';

export function Insignia({
  tono,
  children,
}: {
  tono: 'exito' | 'peligro' | 'aviso' | 'neutro' | 'info';
  children: ReactNode;
}) {
  const tonos = {
    exito: 'text-success bg-success/15',
    peligro: 'text-destructive bg-destructive/15',
    aviso: 'text-warning bg-warning/15',
    info: 'text-info bg-info/15',
    neutro: 'text-muted bg-surface',
  } as const;
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-etiqueta font-bold ${tonos[tono]}`}>
      {children}
    </span>
  );
}

/**
 * Marca que un negocio es un entorno sintético. Existe como componente y no como texto suelto
 * porque el mismo chip se dibuja en el menú de cuenta y en la tabla del panel, y llegó a decir tres
 * cosas distintas.
 */
export function ChipPrueba() {
  return <Insignia tono="info">prueba</Insignia>;
}

/**
 * Elegir una de varias opciones, con un solo aspecto en todos los sitios que la usan. El activo va
 * con borde y fondo teñido: el relleno oscuro significa «aquí estás» y es solo de la navegación.
 */
export function GrupoOpciones<T extends string>({
  etiqueta,
  opciones,
  valor,
  onElegir,
  e2e,
  className = '',
}: {
  /** Para lectores de pantalla: qué se está eligiendo. */
  etiqueta: string;
  /** `apagada` la muestra sin dejar elegirla. */
  opciones: { valor: T; texto: string; icono?: string; e2e?: string; apagada?: boolean }[];
  valor: T | null;
  onElegir: (valor: T) => void;
  /** Marca cada opción cuando no traen su propio `e2e`. */
  e2e?: (opcion: { valor: T; texto: string }) => string;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} role="radiogroup" aria-label={etiqueta}>
      {opciones.map((o) => {
        const activa = o.valor === valor;
        return (
          <button
            key={o.valor}
            role="radio"
            aria-checked={activa}
            disabled={o.apagada}
            onClick={() => onElegir(o.valor)}
            data-e2e={o.e2e ?? e2e?.(o)}
            className={`rounded-control min-h-(--alto-control) inline-flex items-center gap-2 border px-4 py-1.5 text-contenido font-bold ${
              activa
                ? 'border-primary bg-primary/15 text-primary'
                : 'border-border bg-card text-muted hover:text-foreground'
            } ${o.apagada ? 'cursor-not-allowed opacity-40' : ''}`}
          >
            {o.icono && <span aria-hidden>{o.icono}</span>}
            {o.texto}
          </button>
        );
      })}
    </div>
  );
}
